import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type TypeOptions, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useToast } from '@pulsepointinc/bi-components';
import { io } from 'socket.io-client';
import { useCurrentWorkspace } from '../hooks/useCurrentWorkspace';
import { getWSBaseURL } from '../lib/api';
import { useUserData } from '../modules/GenomeWorkspaceView/LeftView/hooks/useUserData';

interface SocketConnectionProviderData {
  isConnected: boolean;
  connectToSockets: () => void;
  disconnectFromSockets: () => void;
  notifications: INotification[];
}
export interface INotification {
  workspaceID: number;
  notificationHeader: string;
  notificationBody: string;
  notificationStatus: TypeOptions;
  email: string;
  eventID: string;
}

export const socket = io(getWSBaseURL());

export const SocketConnectionContext = createContext<SocketConnectionProviderData>({
  isConnected: false,
  connectToSockets: () => {},
  disconnectFromSockets: () => {},
  notifications: [],
});

export const SocketConnectionContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isConnected, setIsConnected] = useState<boolean>(socket.connected);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [processedEvents, setProcessedEvents] = useState<Record<string, boolean>>({});
  const { userData } = useUserData('userEmail');
  const userEmail = userData.userEmail;
  const { showError, showSuccess, showWarning, showInfo } = useToast();
  const { currentWorkspace } = useCurrentWorkspace();

  const handleWorkspaceNotification = useCallback(
    (value: INotification) => {
      if (currentWorkspace?.workspace_id === value.workspaceID) {
        showNotification(value);
      }
    },
    [currentWorkspace?.workspace_id],
  );

  const handleUserNotification = useCallback(
    (value: INotification) => {
      if (value.email === userEmail) {
        showNotification(value);
      }
    },
    [userData.userEmail],
  );

  const handleToast = (value: INotification) => {
    const { notificationHeader, notificationBody, notificationStatus } = value;
    switch (notificationStatus) {
      case 'success':
        showSuccess(notificationBody || notificationHeader);
        break;
      case 'error':
        showError(notificationBody || notificationHeader);
        break;
      case 'warning':
        showWarning(notificationBody || notificationHeader);
        break;
      default:
        showInfo(notificationBody || notificationHeader);
    }

    return null;
  };

  const showNotification = (value: INotification) => {
    setNotifications((previous) => [...previous, value]);

    toast((props) => handleToast(props.data), {
      data: value,
      closeButton: false,
      icon: false,
      style: {
        padding: 0,
        margin: 0,
        minHeight: 'auto',
      },
      bodyStyle: {
        padding: 0,
        margin: 0,
        borderRadius: '6px',
      },
    });
  };

  const connectToSockets = () => {
    socket.connect();
  };

  const disconnectFromSockets = () => {
    socket.disconnect();
  };

  useEffect(() => {
    const onConnect = () => {
      setIsConnected(true);
    };

    const onDisconnect = () => {
      setIsConnected(false);
    };

    const handleNotificationEvent = (value: INotification) => {
      if (processedEvents[value.eventID]) {
        return;
      }

      setProcessedEvents((prev) => ({
        ...prev,
        [value.eventID]: true,
      }));

      const isUserNotification = value.email === userEmail;

      if (isUserNotification) {
        handleUserNotification(value);
      } else if (currentWorkspace?.workspace_id === value.workspaceID) {
        handleWorkspaceNotification(value);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('operation-status', handleNotificationEvent);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('operation-status', handleNotificationEvent);
    };
  }, [currentWorkspace?.workspace_id, handleUserNotification, handleWorkspaceNotification, processedEvents, userEmail]);

  const contextValue = useMemo(
    () => ({
      isConnected,
      disconnectFromSockets,
      connectToSockets,
      notifications,
    }),
    [isConnected, notifications],
  );

  return <SocketConnectionContext.Provider value={contextValue}>{children}</SocketConnectionContext.Provider>;
};

export const useSocketConnection = () => {
  return useContext<SocketConnectionProviderData>(SocketConnectionContext);
};
